import React from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import SolitaireCore from "../SolitaireCore";
import {
  GhostCards,
  DRAG_THRESHOLD_PX,
  parseSourceTag,
  sameSource,
  cardsForSource,
  sourceElements,
  hideElements,
  restoreElements,
} from "../SolitaireCore/dragKit";
import DeckPicker from "../DeckPicker";
import useKey from "../../hooks/useKey";
import {
  replay,
  tryApplyWithIndex,
  isWon,
  spriteKey,
  MOVE_TYPE,
  PILE,
} from "../../engine/solitaire";
import { cardPlace, cardFlip } from "../../sound/sound";
import { bumpStat } from "../../stats";

// The player's own game. Owns engine state + selection + click resolution;
// hands rendering to SolitaireCore. Positioning lives in the Stage, so this
// component produces a plain unpositioned game that can be dropped anywhere.
//
// Resolution rules:
//   - Stock click always applies DRAW; clears selection.
//   - If there's a selection and the clicked thing is a legal destination:
//     apply move, clear selection.
//   - If the click is on the already-selected source: deselect.
//   - Otherwise: if the clicked thing is a valid source, select it (illegal
//     drop attempts fall through to reselection instead of erroring).
//     Empty-column clicks are destination-only.
//   - ESC always clears the selection.

// Drag presentation (ghost stack, source tags, hide/restore) is shared
// with SpectatedSolitaire via ../SolitaireCore/dragKit — this file owns
// only the pointer-event input adapter and move resolution.

function PlayableSolitaire({
  title,
  gameId,
  cardBack,
  sendMove,
  sendUndo,
  dealNewGame,
  initialMoves,
  initialCanUndo = false,
  // Presence: relays this player's mouse position to their watchers.
  sendCursor,
  // Injected by the Stage: focus, title-bar buttons, Help → About hook.
  inactive = false,
  onControlClick,
  onHelpAbout,
  // Which title-bar buttons are honestly grey. Desktop disables only
  // maximize; compact also greys minimize (nowhere to minimize TO).
  disabledControls = ["maximize"],
}) {
  // gameId is the seed — one deck per game ID via the engine's Lehmer
  // unranking bijection. `key={gameId}` in the parent fully remounts this
  // component with a fresh deck whenever the game ID changes.
  //
  // initialMoves is the server's move history, used to resume after a
  // refresh; read once in the lazy init, ignored thereafter.
  const [state, setState] = React.useState(() =>
    replay(gameId, initialMoves ?? [], { label: "PlayableSolitaire" }),
  );
  const [selection, setSelection] = React.useState(null);
  // Foundation copies (engine ints) snapshotted at the win; the cascade
  // pops cards off these as it launches them so the rendered stacks
  // shrink underneath the flying cards. null = not celebrating.
  const [celebrationFoundations, setCelebrationFoundations] =
    React.useState(null);
  // A resumed game may already be won (refresh right after winning).
  const alreadyWon = React.useRef(isWon(state));

  // Single-step undo: the state before the most recent move, or null.
  // Cleared by using it — an undo can never follow an undo. A resumed
  // game rebuilds its armed undo by replaying one move short.
  const undoStateRef = React.useRef(null);
  // Whether the most recent applied op was a draw — undo needs this so
  // the idle-clock mirror treats undoing a draw as stock-cycling too.
  const lastAppliedWasDrawRef = React.useRef(false);
  const [canUndo, setCanUndo] = React.useState(() => {
    if (initialCanUndo && (initialMoves?.length ?? 0) > 0) {
      undoStateRef.current = replay(gameId, initialMoves.slice(0, -1), {
        label: "PlayableSolitaire:undo-arm",
      });
      return true;
    }
    return false;
  });

  // The chosen deck (Game → Deck…): prop is the initial value, the
  // dialog updates + persists it.
  const [deck, setDeck] = React.useState(cardBack);
  const [deckOpen, setDeckOpen] = React.useState(false);

  useKey("Escape", () => setSelection(null));

  const startCelebration = (foundations) => {
    setSelection(null);
    setCelebrationFoundations(foundations.map((f) => f.slice()));
  };

  // A game counts as "played" at your FIRST move, not at the deal — a
  // refresh-resume arrives with history, so it starts pre-counted.
  const gameCountedRef = React.useRef((initialMoves?.length ?? 0) > 0);

  React.useEffect(() => {
    if (alreadyWon.current) {
      startCelebration(state.foundations);
    } else if (
      // Dev knob: `?celebrate` fires the cascade immediately with four full
      // foundations, so the animation can be eyeballed without winning.
      new URLSearchParams(window.location.search).has("celebrate")
    ) {
      startCelebration(
        [0, 1, 2, 3].map((suit) =>
          Array.from({ length: 13 }, (_, rank) => suit * 13 + rank),
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPopTop = (foundationIdx) => {
    setCelebrationFoundations((prev) => {
      if (!prev) return prev;
      const next = prev.map((f) => f.slice());
      next[foundationIdx].pop();
      return next;
    });
  };

  const applyAndRelay = (move, releaseAt = null) => {
    // Once the cascade starts, the popped-down celebration foundations no
    // longer match engine state — ignore further input.
    if (celebrationFoundations != null) return null;
    const result = tryApplyWithIndex(state, move);
    if (result == null) return null;
    if (move.type === MOVE_TYPE.DRAW) cardFlip();
    else cardPlace();
    // Local stats ledger; best-effort, undo doesn't un-count.
    if (!gameCountedRef.current) {
      gameCountedRef.current = true;
      bumpStat("games");
    }
    bumpStat(move.type === MOVE_TYPE.DRAW ? "draws" : "moves");
    undoStateRef.current = state;
    lastAppliedWasDrawRef.current = move.type === MOVE_TYPE.DRAW;
    setCanUndo(true);
    setState(result.next);
    if (sendMove)
      sendMove(result.index, releaseAt, {
        draw: move.type === MOVE_TYPE.DRAW,
      });
    if (isWon(result.next)) {
      bumpStat("wins");
      startCelebration(result.next.foundations);
    }
    return result.next;
  };

  const tryDestination = (to) => {
    if (!selection) return false;
    const next = applyAndRelay({ type: MOVE_TYPE.MOVE, from: selection, to });
    if (next == null) return false;
    setSelection(null);
    // A successful drop is a completed gesture — must not seed a
    // double-click on the card it just placed.
    lastClickRef.current = { key: null, at: 0 };
    return true;
  };

  const selectOrDeselect = (source) => {
    if (sameSource(selection, source)) setSelection(null);
    else setSelection(source);
  };

  // Double-click a card to auto-play it to a foundation (classic MS).
  // Tracked by timing rather than onDoubleClick so the first click can
  // still select normally, and an unplayable double-click degrades to
  // the usual select/deselect toggle. EVERY click target must stamp
  // this (noteClick), not just the auto-playable ones, or an unrelated
  // pair of clicks on the same key within 400ms reads as a double-click.
  const lastClickRef = React.useRef({ key: null, at: 0 });
  const noteClick = (key) => {
    lastClickRef.current = { key, at: Date.now() };
  };
  const isDoubleClick = (key) => {
    const now = Date.now();
    const isDouble =
      lastClickRef.current.key === key && now - lastClickRef.current.at < 400;
    lastClickRef.current = { key: isDouble ? null : key, at: now };
    return isDouble;
  };
  const tryAutoFoundation = (from) => {
    for (let f = 0; f < 4; f++) {
      const next = applyAndRelay({
        type: MOVE_TYPE.MOVE,
        from,
        to: { pile: PILE.FOUNDATION, index: f },
      });
      if (next != null) {
        setSelection(null);
        return true;
      }
    }
    return false;
  };

  const doUndo = () => {
    if (undoStateRef.current == null || celebrationFoundations != null) return;
    // Ctrl+Z with a card in hand: land it first — the board is about to
    // be replaced under the gesture, and a drag over nodes React may
    // recreate would strand hidden cards and drop against a stale source.
    if (dragSessionRef.current) {
      clearLiftVisuals(dragSessionRef.current);
      dragSessionRef.current = null;
    }
    cardPlace();
    setState(undoStateRef.current);
    undoStateRef.current = null;
    setCanUndo(false);
    setSelection(null);
    if (sendUndo) sendUndo({ draw: lastAppliedWasDrawRef.current });
  };
  useKey(
    "z",
    (e) => {
      // Not Shift+Z (redo's chord) and not while a neighbor's window
      // has focus (Ctrl+Z acts on the window you're IN).
      if (inactive || e.shiftKey) return;
      if (e.ctrlKey || e.metaKey) doUndo();
    },
    { caseless: true }, // CapsLock reports "Z"; the chord still counts
  );

  const onDrawClick = () => {
    noteClick("stock");
    setSelection(null);
    applyAndRelay({ type: MOVE_TYPE.DRAW });
  };

  const onWasteClick = () => {
    if (state.waste.length === 0) return;
    if (isDoubleClick("waste") && tryAutoFoundation({ pile: PILE.WASTE })) {
      return;
    }
    selectOrDeselect({ pile: PILE.WASTE });
  };

  const onFoundationClick = (i) => {
    noteClick(`foundation:${i}`);
    if (tryDestination({ pile: PILE.FOUNDATION, index: i })) return;
    if (state.foundations[i].length === 0) return;
    selectOrDeselect({ pile: PILE.FOUNDATION, index: i });
  };

  const onTableauCardClick = (col, depth) => {
    if (depth !== 0) {
      // Deep cards can't auto-play; distinct key so a deep click can't
      // pair with a top-card click into a phantom double.
      noteClick(`tableau:${col}:${depth}`);
    } else if (
      isDoubleClick(`tableau:${col}`) &&
      tryAutoFoundation({ pile: PILE.TABLEAU, index: col, depth: 0 })
    ) {
      return;
    }
    if (tryDestination({ pile: PILE.TABLEAU, index: col })) return;
    selectOrDeselect({ pile: PILE.TABLEAU, index: col, depth });
  };

  const onTableauEmptyClick = (col) => {
    noteClick(`empty:${col}`);
    tryDestination({ pile: PILE.TABLEAU, index: col });
  };

  // ------------------------------------------------------------------
  // Drag-and-drop (classic MS behavior, coexisting with click-select).
  // Pointer down on a card arms a potential drag; crossing the movement
  // threshold lifts the card (or the partial run below it) into a ghost
  // that follows the pointer. Release over a tableau/foundation cell
  // attempts the move; anywhere else snaps back instantly (no return
  // animation). A completed drag suppresses the click that browsers
  // fire after pointerup, so click-select never sees it.
  // ------------------------------------------------------------------
  const rootRef = React.useRef(null);
  const ghostRef = React.useRef(null);
  // The lift's slide-up wrapper inside the ghost (see GhostRiser).
  const riserRef = React.useRef(null);
  const dragSessionRef = React.useRef(null);
  // Set by the cursor-presence effect below (server/PROTOCOL.md §4):
  // releaseCursorRef sends a BARE release edge (abandoned drag / plain
  // click); takeReleaseRef consumes the release WITHOUT sending, for
  // drags that complete as a move — the release coords ride ON the move
  // message ({..., c:[x,y,0]}) so spectators get release+move as one
  // atomic frame, avoiding a drop flicker from two separate frames.
  const releaseCursorRef = React.useRef(null);
  const takeReleaseRef = React.useRef(null);
  const sendCursorRawRef = React.useRef(null);
  // The just-finished drag's release point, or null: the one click the
  // browser echoes after a drag is swallowed by proximity, not timing
  // (see finish()).
  const suppressClickRef = React.useRef(null);
  // {cards: [sprite names], x, y} while a drag is live.
  const [drag, setDrag] = React.useState(null);

  const stateRef = React.useRef(state);
  stateRef.current = state;
  const applyRef = React.useRef(null);
  applyRef.current = (move, releaseAt) => {
    const next = applyAndRelay(move, releaseAt);
    if (next != null) setSelection(null);
    return next;
  };

  // TOUCH_LIFT: a fingertip hides exactly the card it's moving, so touch
  // interactions lift the grabbed cards above the touch point. The lift
  // arms on a short HOLD, not on drag start (the card should already be
  // up when you start moving) — but never on a quick tap: taps release
  // before the hold fires, so tap-to-select and double-tap-to-foundation
  // see the board untouched (a lifted card under a click hit-tests wrong).
  const TOUCH_LIFT_HOLD_MS = 160;
  // Lift amount is a function of WHERE on the card the finger pressed: a
  // run grab presses the thin exposed sliver at a card's top edge (the
  // finger covers the face, needing a big lift to show which card is
  // held), while a single card is grabbed near its middle with its face
  // already half-visible. One rule covers both: lift so the card's TOP
  // rides ~70% of a card above the finger, clamped to a 15%-55% lift.
  //
  // The lift is rendered through the DRAG GHOST from the moment the hold
  // fires — the real tiles are never transformed. An in-flow transform
  // promotes a tile to its own compositing layer, and under the compact
  // stage's fractional scale that re-snaps neighboring cards to different
  // device pixels. The ghost overlay + visibility-hidden sources are the
  // same machinery every drag uses, proven shift-free.
  const touchLiftPx = (h, grabOffY) =>
    Math.round(Math.min(Math.max(h * 0.7 - grabOffY, h * 0.15), h * 0.55));

  // Set each render by the drag effect below: mounts the lift/drag
  // visuals for a session (hold timers live outside the effect).
  const mountVisualsRef = React.useRef(null);

  // The one teardown for a session's visuals, pending or active: real
  // cards back INSTANTLY and synchronously (on a release this runs in
  // the pointerup handler, before the browser hit-tests the compat
  // click — the ghost is pointer-events: none so it can't shadow the
  // card), ghost unmounted, hold timer dead.
  const clearLiftVisuals = (d) => {
    clearTimeout(d.holdTimer);
    if (!d.visualsMounted) return;
    restoreElements(d.hidden);
    d.hidden = [];
    d.visualsMounted = false;
    setDrag(null);
  };

  const onRootPointerDown = (e) => {
    // A fresh press voids any armed click suppressor: the suppressor
    // exists to swallow a release's own echo click, and an echo always
    // dispatches before the next press reaches pointerdown. iOS often
    // fires NO echo at all for a lifted put-back, so a stale suppressor
    // would otherwise eat the next real tap whenever it landed within
    // 12px/400ms or resolved to the same card.
    suppressClickRef.current = null;
    if (celebrationFoundations != null) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const cur = dragSessionRef.current;
    if (cur?.active) {
      // One drag at a time: while a drag is in flight, other fingers
      // are bystanders — replacing the session here would orphan the
      // ghost and leave the real cards hidden forever.
      if (e.pointerId !== cur.pointerId) return;
      // The SAME pointer pressing again means its release was lost
      // (shouldn't happen under capture + cancel + blur, but a stuck
      // session must not brick dragging): reset the stray drag.
      clearLiftVisuals(cur);
      dragSessionRef.current = null;
    } else if (cur) {
      // A second finger landing mid-press must not orphan the first
      // session's lift/timer.
      clearLiftVisuals(cur);
    }
    const el = e.target.closest?.("[data-card-source]");
    if (!el || !rootRef.current?.contains(el)) return;
    const src = parseSourceTag(el.getAttribute("data-card-source"));
    if (!src) return;
    const d = {
      src,
      grabEl: el,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      // Touch lifts the cards above the finger (see TOUCH_LIFT above);
      // a mouse pointer doesn't occlude, so it drags flush.
      touch: e.pointerType === "touch",
      active: false,
      hidden: [],
    };
    if (d.touch) {
      d.holdTimer = setTimeout(() => {
        // Only if this press is still the pending session: a tap
        // released, a replaced session, or an already-active drag
        // (which mounted the visuals itself) all skip.
        if (dragSessionRef.current === d && !d.active && !d.visualsMounted) {
          mountVisualsRef.current?.(d, d.startX, d.startY);
        }
      }, TOUCH_LIFT_HOLD_MS);
    }
    dragSessionRef.current = d;
  };

  React.useEffect(() => {
    const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), Math.max(lo, hi));
    // Ghost position in FELT-local px: the grabbed card pinned inside
    // the felt no matter where the pointer goes.
    const ghostPos = (d, e) => ({
      x:
        (clamp(e.clientX - d.offX, d.felt.left, d.felt.right - d.cardW) -
          d.felt.left) /
        d.scale,
      y:
        (clamp(e.clientY - d.offY, d.felt.top, d.felt.bottom - d.cardH) -
          d.felt.top) /
        d.scale,
    });

    // Mount the lift/drag visuals at (atX, atY): measure the scene,
    // put the ghost up (lifted for touch, flush for mouse), hide the
    // real cards. Used by BOTH the hold timer (press-and-hold, drag
    // not yet real) and threshold activation (fast drags that outran
    // the hold) — one system, so there is no handoff to get wrong.
    //
    // The ghost is confined to the FELT: the grabbed card is clamped
    // fully inside the green surface (a single card can't leave it at
    // all), and a dragged run's fanned tail clips at the felt edge.
    // Measure once here; the window can't move mid-press (the pointer
    // is on the card), so the rects are stable.
    const mountVisuals = (d, atX, atY) => {
      // The lift is a MODE BOUNDARY: once the card visibly rises you
      // are HOLDING it — any standing selection clears now, and
      // releasing without dragging puts the card back rather than
      // selecting (see finish). Only quick taps — which never lift —
      // select.
      setSelection(null);
      const rootRect = rootRef.current.getBoundingClientRect();
      const feltEl = rootRef.current.querySelector("[data-felt]");
      d.felt = (feltEl ?? rootRef.current).getBoundingClientRect();
      // Guards against a CSS-scaled copy of this window ever existing
      // (mobile compact, --ui-zoom ≠ 1); today scale is 1.
      d.scale = rootRect.width / rootRef.current.offsetWidth || 1;
      const rect = d.grabEl.getBoundingClientRect();
      d.cardW = rect.width;
      d.cardH = rect.height;
      d.offX = d.startX - rect.left;
      d.offY = d.startY - rect.top;
      // Folding the lift into offY means every downstream computation
      // — felt clamp, ghost position, run-tail clip, drop resolution
      // — sees the lifted card (see TOUCH_LIFT above).
      d.lift = d.touch ? touchLiftPx(d.cardH, d.offY) : 0;
      d.offY += d.lift;
      // The clip layer sits exactly over the felt, in root-local px.
      d.clip = {
        left: (d.felt.left - rootRect.left) / d.scale,
        top: (d.felt.top - rootRect.top) / d.scale,
        width: d.felt.width / d.scale,
        height: d.felt.height / d.scale,
      };
      // Ghost FIRST (synchronously — this can run in a native window
      // listener, where React flushes state in a microtask that can
      // slip past a paint), THEN hide the sources: reversing the order
      // leaves a ghostless, cardless frame at drag start.
      // The session keeps a handle on the state object so onMove can
      // keep its x/y current (see the re-render note there).
      d.dragState = {
        cards: cardsForSource(stateRef.current, d.src),
        clip: d.clip,
        ...ghostPos(d, { clientX: atX, clientY: atY }),
      };
      ReactDOM.flushSync(() => {
        setDrag(d.dragState);
      });
      d.hidden = sourceElements(rootRef.current, d.src, d.grabEl);
      hideElements(d.hidden);
      d.visualsMounted = true;
      // The rise is IMPERATIVE only, set before this task's paint: mount
      // at the finger, then ease up to riding height (top on the riser —
      // see GhostRiser for why never a transform). It must not live in
      // the React style prop — a mid-drag re-render would re-assert the
      // stale mount value and drop the risen ghost back down.
      // Only a stationary press-and-hold ANIMATES the rise. A fast drag
      // (threshold activation, d.active already true) mounts already
      // risen — animating from the finger-flush position would paint the
      // card below its rest spot and ease it up while the finger keeps
      // moving. The riser's default top is 0 (risen), so skipping the
      // assignments entirely is correct there.
      if (d.lift > 0 && riserRef.current && !d.active) {
        riserRef.current.style.top = d.lift / d.scale + "px";
        // Double rAF: a style change in the mount frame coalesces
        // with the initial paint and the transition never plays.
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            if (riserRef.current) riserRef.current.style.top = "0px";
          }),
        );
      }
    };
    mountVisualsRef.current = mountVisuals;

    const onMove = (e) => {
      const d = dragSessionRef.current;
      if (!d) return;
      // The session belongs to ONE pointer: a palm edge or resting
      // second finger must neither steer the ghost nor activate the
      // drag. (Synthetic events without a pointerId pass — see finish.)
      if (e.pointerId != null && e.pointerId !== d.pointerId) return;
      if (!d.active) {
        // Stale session self-heal: a sub-threshold press whose release
        // happened outside the browser window never saw pointerup —
        // without this, wandering back across the board with the button
        // UP would start a drag out of thin air.
        if (e.buttons === 0) {
          clearLiftVisuals(d);
          dragSessionRef.current = null;
          return;
        }
        const dist = Math.hypot(e.clientX - d.startX, e.clientY - d.startY);
        if (dist < DRAG_THRESHOLD_PX) return;
        d.active = true;
        // (Selection clears in mountVisuals — every grab passes
        // through it, held or fast.)
        // Capture only once the drag is real: it guarantees move/up
        // delivery even outside the browser window (release past the
        // screen edge used to strand the ghost). Capturing on the
        // pointerDOWN would retarget the compat click and break plain
        // click-to-select, so it waits for the threshold.
        try {
          rootRef.current.setPointerCapture(d.pointerId);
        } catch {
          // pointer already gone (e.g. pen lifted) — window listeners
          // still cover the common paths.
        }
        // A held press already put the ghost up (the hold timer); a
        // fast drag mounts it here and its slide-up plays en route.
        if (!d.visualsMounted) {
          clearTimeout(d.holdTimer);
          mountVisuals(d, e.clientX, e.clientY);
        }
        return;
      }
      e.preventDefault();
      const el = ghostRef.current;
      if (el) {
        const p = ghostPos(d, e);
        el.style.left = p.x + "px";
        el.style.top = p.y + "px";
        // Keep the state object current too: React re-applies its
        // style prop on any re-render (the desktop re-renders under
        // us all the time), and a stale mount position would teleport
        // a paused ghost back to where the drag began.
        if (d.dragState) {
          d.dragState.x = p.x;
          d.dragState.y = p.y;
        }
      }
    };

    const finish = (e, cancelled) => {
      const d = dragSessionRef.current;
      if (!d) return;
      // A second finger lifting somewhere must not drop the card at
      // ITS coordinates (or end a pending press). Synthetic finishes —
      // blur's {} and Escape's keyboard event — carry no pointerId and
      // always pass: they are how a stuck session gets cleaned up.
      if (e.pointerId != null && e.pointerId !== d.pointerId) return;
      dragSessionRef.current = null;
      if (!d.active) {
        // A LIFTED press that never dragged is a put-back, not a tap:
        // once the card visibly rose, selecting on release read as a
        // flicker, and iOS made the outcome a coin flip anyway
        // (long-press releases sometimes fire no click; a finger
        // rolling >5px on liftoff converts to a micro-drag). Swallow
        // the echo so every lifted path ends the same: card home,
        // nothing selected. Quick taps never lift and remain the one
        // road to selection — their click passes untouched.
        if (d.visualsMounted && e.clientX != null) {
          suppressClickRef.current = {
            x: e.clientX,
            y: e.clientY,
            t: performance.now(),
            src: d.src,
          };
        }
        // Visuals clear synchronously HERE, in the pointerup handler,
        // so any click that does pass hit-tests the restored real card
        // (the ghost is pointer-events: none and can't shadow it).
        clearLiftVisuals(d);
        return;
      }
      try {
        rootRef.current?.releasePointerCapture(d.pointerId);
      } catch {
        // already released (implicit on pointerup) — fine
      }
      // Consume the release edge: if the drop below resolves to a move,
      // the coords ride the move message; any early return past this
      // point is an abandoned drag and sends the bare edge instead.
      const releaseAt = takeReleaseRef.current?.() ?? null;
      const bareRelease = () => {
        if (releaseAt) sendCursorRawRef.current?.(releaseAt, false);
      };
      // Swallow the click browsers fire right after pointerup — but ONLY
      // that one, identified by WHERE it lands, not by when: post-drag
      // clicks dispatch synchronously on mouse but async on iOS, where
      // input events can outrun a fixed timer. A click within a card's
      // reach of the release point inside 400ms is the drag's own echo;
      // anything else is a real click. A SYNTHETIC finish (Escape, blur
      // — no client coords) has no echo coming, so it must not arm the
      // suppressor: with the src match below it would eat a real click
      // on that card right after.
      if (e.clientX != null) {
        suppressClickRef.current = {
          x: e.clientX,
          y: e.clientY,
          t: performance.now(),
          // The source the drag grabbed: its echo click must never
          // select the dragged card, even when it lands outside the
          // proximity window (see onClickCapture).
          src: d.src,
        };
      }
      clearLiftVisuals(d);
      if (cancelled) return bareRelease();
      // Resolve the drop at CLAMPED points: the card is pinned to the
      // felt, so a release with the pointer past the edge lands where
      // the card visibly is. A lifted touch drag is generous: the
      // visible top card's center is tried first, the finger point
      // second, and the first candidate whose move is LEGAL wins — a
      // near-miss on one pile doesn't waste a legal drop on the other.
      const dropPoints = [];
      if (d.lift) {
        dropPoints.push({
          x:
            clamp(e.clientX - d.offX, d.felt.left, d.felt.right - d.cardW) +
            d.cardW / 2,
          y:
            clamp(e.clientY - d.offY, d.felt.top, d.felt.bottom - d.cardH) +
            d.cardH / 2,
        });
      }
      dropPoints.push({ x: e.clientX, y: e.clientY });
      const tried = new Set();
      for (const pt of dropPoints) {
        const under = document.elementFromPoint(
          clamp(pt.x, d.felt.left + 1, d.felt.right - 1),
          clamp(pt.y, d.felt.top + 1, d.felt.bottom - 1),
        );
        const target = under?.closest?.("[data-drop]");
        if (!target || !rootRef.current?.contains(target)) continue;
        const drop = target.getAttribute("data-drop");
        if (tried.has(drop)) continue;
        tried.add(drop);
        const [kind, idx] = drop.split(":");
        const to =
          kind === "f"
            ? { pile: PILE.FOUNDATION, index: Number(idx) }
            : { pile: PILE.TABLEAU, index: Number(idx) };
        const applied = applyRef.current(
          { type: MOVE_TYPE.MOVE, from: d.src, to },
          releaseAt,
        );
        if (applied != null) return;
      }
      // No candidate landed a move: the release edge still must go out.
      bareRelease();
    };

    const onUp = (e) => finish(e, false);
    const onCancel = (e) => finish(e, true);
    // Escape mid-drag: cards snap home, no move, spectators see the
    // hand let go (bare release edge) — same path as pointercancel.
    const onKeyDown = (e) => {
      if (e.key === "Escape" && dragSessionRef.current?.active) {
        finish(e, true);
      }
    };
    // Focus loss mid-drag (cmd-tab, alt-tab): the release will land in
    // another app, so treat it as a cancel now rather than a stuck ghost.
    const onBlur = () => {
      if (dragSessionRef.current?.active) finish({}, true);
      else {
        if (dragSessionRef.current) clearLiftVisuals(dragSessionRef.current);
        dragSessionRef.current = null;
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  // While celebrating, render the popped-down foundation copies so stacks
  // shrink as cards fly; the rest of the board stays frozen at the win.
  const displayState =
    celebrationFoundations != null
      ? { ...state, foundations: celebrationFoundations }
      : state;

  const gameMenu = {
    label: "Game",
    items: [
      {
        label: "Undo",
        shortcut: "Ctrl+Z",
        onClick: doUndo,
        disabled: !canUndo || celebrationFoundations != null,
      },
      {
        label: "Deck…",
        onClick: () => setDeckOpen(true),
      },
      {
        label: "Deal New Game",
        onClick: () => dealNewGame && dealNewGame(),
        disabled: !dealNewGame,
      },
    ],
  };

  // sceneTargets: the first-visit boot scene's ghost cursor opens About
  // through this real menu (Stage aims at the data-scene-target attrs
  // and fires real .click()s — only YOUR window carries the tags).
  const helpMenu = {
    label: "Help",
    sceneTarget: "menu-help",
    items: [
      {
        label: "About Solitaire Alone Together…",
        sceneTarget: "menu-help-about",
        onClick: onHelpAbout,
        disabled: !onHelpAbout,
      },
    ],
  };

  // Cursor presence: window-local coordinates in the fixed 620x435
  // space, throttled client-side (the server throttles again). The
  // watcher's copy of this window is the same size, so px map 1:1.
  // `down` mirrors the held button; press/release edges send
  // immediately (a throttled-away release would leave watchers seeing
  // a hand stuck holding a card).
  const lastCursorSentRef = React.useRef(0);
  const cursorDownRef = React.useRef(false);
  const lastCursorPosRef = React.useRef(null);
  // True while our hand is "on the table" (any move/press since the last
  // leave edge). Gates the keepalive below.
  const cursorPresentRef = React.useRef(false);
  const windowCursorPos = (e) => {
    const root = rootRef.current;
    if (!root) return null;
    const rect = root.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    return {
      x: Math.round(((e.clientX - rect.left) / rect.width) * 620),
      y: Math.round(((e.clientY - rect.top) / rect.height) * 435),
    };
  };
  const sendCursorNow = (pos, down) => {
    if (!sendCursor || !pos) return;
    lastCursorSentRef.current = performance.now();
    sendCursor(pos.x, pos.y, down);
  };
  const onRootPointerMove = (e) => {
    if (!sendCursor) return;
    const pos = windowCursorPos(e);
    if (!pos) return;
    lastCursorPosRef.current = pos;
    cursorPresentRef.current = true;
    if (performance.now() - lastCursorSentRef.current < 110) return;
    sendCursorNow(pos, cursorDownRef.current);
  };
  const onCursorPressDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    cursorDownRef.current = true;
    const pos = windowCursorPos(e) ?? lastCursorPosRef.current;
    lastCursorPosRef.current = pos;
    cursorPresentRef.current = true;
    sendCursorNow(pos, true);
  };
  // Keepalive: watchers evaporate a cursor 8s after its last frame (a
  // severed player never sends a leave edge, so silence must eventually
  // mean gone). A real idle hand should stay honestly parked, so re-send
  // the last frame every 5s while present; only the leave edge stops it.
  // Hidden-tab throttling means a long-backgrounded hand can blink for
  // watchers — accepted, since that case isn't really "at the table"
  // anyway; this exists for the brief-silence/wifi-blip case.
  React.useEffect(() => {
    if (!sendCursor) return undefined;
    const t = setInterval(() => {
      const pos = lastCursorPosRef.current;
      if (!cursorPresentRef.current || !pos) return;
      sendCursorNow(pos, cursorDownRef.current);
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendCursor]);
  // Releases happen anywhere (drags end outside the window), so listen
  // globally while mounted. Idempotent (the down ref gates it), so the
  // drag path calling it early via releaseCursorRef is safe — this
  // listener then no-ops.
  React.useEffect(() => {
    const release = () => {
      if (!cursorDownRef.current) return;
      cursorDownRef.current = false;
      sendCursorNow(lastCursorPosRef.current, false);
    };
    releaseCursorRef.current = release;
    // Consume the held state and hand back the release coords WITHOUT
    // sending — the caller attaches them to the move message.
    takeReleaseRef.current = () => {
      if (!cursorDownRef.current) return null;
      cursorDownRef.current = false;
      const pos = lastCursorPosRef.current;
      return pos ? [pos.x, pos.y] : null;
    };
    sendCursorRawRef.current = ([x, y], down) =>
      sendCursorNow({ x, y }, down);
    window.addEventListener("pointerup", release);
    window.addEventListener("pointercancel", release);
    return () => {
      window.removeEventListener("pointerup", release);
      window.removeEventListener("pointercancel", release);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Root
      ref={rootRef}
      onPointerDown={(e) => {
        onCursorPressDown(e);
        onRootPointerDown(e);
      }}
      onPointerMove={onRootPointerMove}
      onPointerLeave={() => {
        cursorPresentRef.current = false;
        if (sendCursor) sendCursor(-1, -1, false);
      }}
      onClickCapture={(e) => {
        // While a drag is in flight, clicks don't exist: the dragging
        // pointer can't click (its release ends the session first),
        // so any click here is a bystander finger — which must not
        // select a card mid-drag, and MUST not double-tap a card onto
        // a foundation while the board is mid-gesture.
        if (dragSessionRef.current?.active) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        const s = suppressClickRef.current;
        if (!s) return;
        suppressClickRef.current = null; // one shot, echo or not
        if (performance.now() - s.t >= 400) return;
        // The drag's own echo, identified by WHERE it lands — or by
        // WHAT it lands on: a drag must never end with its own card
        // selected, so a click resolving to the dragged-from source
        // inside the window is swallowed even if the coordinates
        // drifted past the proximity check.
        const clickSrc = parseSourceTag(
          e.target.closest?.("[data-card-source]")?.getAttribute(
            "data-card-source",
          ) ?? "",
        );
        if (
          Math.hypot(e.clientX - s.x, e.clientY - s.y) < 12 ||
          (clickSrc && sameSource(clickSrc, s.src))
        ) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <SolitaireCore
      title={title}
      state={displayState}
      selection={selection}
      menus={[gameMenu, helpMenu]}
      cardBack={deck}
      disabledControls={disabledControls}
      onControlClick={onControlClick}
      inactive={inactive}
      interactive
      onDrawClick={onDrawClick}
      onWasteClick={onWasteClick}
      onFoundationClick={onFoundationClick}
      onTableauCardClick={onTableauCardClick}
      onTableauEmptyClick={onTableauEmptyClick}
      winCelebration={
        celebrationFoundations != null
          ? {
              foundations: celebrationFoundations.map((f) => f.map(spriteKey)),
              seed: gameId,
              onPopTop,
            }
          : null
      }
      />
      {drag && (
        <GhostClip
          aria-hidden="true"
          style={{
            left: drag.clip.left + "px",
            top: drag.clip.top + "px",
            width: drag.clip.width + "px",
            height: drag.clip.height + "px",
          }}
        >
          <DragGhost
            ref={ghostRef}
            data-drag-ghost="true"
            style={{ left: drag.x + "px", top: drag.y + "px" }}
          >
            <GhostRiser ref={riserRef}>
              <GhostCards cards={drag.cards} />
            </GhostRiser>
          </DragGhost>
        </GhostClip>
      )}
      {deckOpen && (
        <DialogOverlay>
          <DeckPicker
            current={deck}
            onChoose={setDeck}
            onClose={() => setDeckOpen(false)}
          />
        </DialogOverlay>
      )}
    </Root>
  );
}

const Root = styled.div`
  position: relative;
`;

// The cards in flight, confined to the FELT: the grabbed card is
// clamped fully inside the green surface (a single card can't leave it
// at all), and a dragged run's fanned tail clips at the felt edge.
// Positioned over the felt rect at drag start; inside the window's own
// stacking context, so overlapping windows still cover it correctly.
const GhostClip = styled.div`
  position: absolute;
  overflow: hidden;
  z-index: 30;
  pointer-events: none;
`;

const DragGhost = styled.div`
  position: absolute;
  pointer-events: none;
`;

// The touch lift's slide-up. NO TRANSFORMS in the local drag path: a
// transform TRANSITION gets promoted to a temporary compositing layer
// for its duration, and inside the compact stage's fractionally-scaled
// subtree that layer's create/destroy re-snaps neighboring cards'
// rasterization. `top` on a relatively-positioned wrapper animates as
// plain repaints instead: no layer, no churn. Mounted at the finger
// (+rise, set imperatively by mountVisuals — never via the React style
// prop, which a mid-drag re-render would re-assert), then eased to 0.
const GhostRiser = styled.div`
  position: relative;
  transition: top 160ms ease-out;
  pointer-events: none;
`;

const DialogOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default PlayableSolitaire;
